import { Platform } from '../../types/content';

/**
 * 앵커 케이스. Hero 다음 화면.
 * 규칙: 시스템·계정·CI 도구 이름, 테이블명, 일일 데이터량, 일정, 조직명은 쓰지 않는다.
 * 재설계는 "계획 중"이므로 "설계했다"까지만 쓴다. 서술 문장은 합쇼체.
 */
export const platform: Platform = {
  headline:
    '열람·매출 데이터를 배치와 실시간으로 수집하고, 가공해, 실무자가 매일 보는 시각화 사이트로 안정적으로 제공하는 데이터 플랫폼',
  scaleContext: '카카오픽코마, 수천만 명이 이용하고 수십만 작품이 연재되는 웹툰 플랫폼',
  problem:
    '2024년 여름, 일본 픽코마에는 DW 데이터 파이프라인이 없었습니다. 수천만 사용자가 읽고 결제하는 플랫폼의 열람·매출 데이터는 여러 시스템에 흩어져 있었고, 원천 수급은 온프레미스에서 클라우드까지 여러 홉을 거치는 새벽 배치 릴레이였습니다. 개별 작업은 돌지만 "어디서 실패했고 어디부터 다시 돌릴지"는 만든 사람만 알았습니다.',
  approach:
    '수집(배치·실시간) → 레이어별 가공 → API → 시각화까지를 하나의 플랫폼으로 처음부터 설계·구축해 운영하며 지표 정의를 마트 레이어에 고정했습니다. 그 위에서 재설계를 시작했습니다. 기준은 셋, 운영 단순성·구조적 통일성·전환 대응력입니다. 실행 도구보다 Raw Landing 계약(Manifest·Watermark·검증)을 먼저 고정하는 방향으로 설계하고, 오케스트레이션과 추출 실행을 분리하는 구조를 잡았습니다. 지금은 검증(PoC) 단계입니다.',
  change:
    '실무자가 SQL 없이 매일 같은 정의의 지표를 봅니다. 이벤트 피크와 장애를 넘기며 제공이 끊기지 않았습니다. 다음 단계는 "누가 만들었는지 알아야 운영되는 파이프라인"을 "계약을 보면 누구나 복구할 수 있는 파이프라인"으로 바꾸는 것입니다.',
  differently:
    '처음부터 파일 도착이 아니라 Manifest 도착을 다음 단계의 시작 조건으로 삼았을 것입니다.',
  designCriteria: [
    {
      title: '운영 단순성',
      description: '실패 지점이 보이고, 필요한 구간만 다시 실행할 수 있으며, 담당자가 바뀌어도 같은 방식으로 대응할 수 있어야 합니다.'
    },
    {
      title: '구조적 통일성',
      description: '테이블마다 스크립트와 예외를 늘리는 대신 공통 계약·경로·검증·재처리 방식을 적용할 수 있어야 합니다.'
    },
    {
      title: '전환 대응력',
      description: '원천의 물리적 위치와 실행 도구가 바뀌어도 착지 이후의 구조를 그대로 유지할 수 있어야 합니다.'
    }
  ],
  collaboration:
    '배치 실행기를 CI 도구에서 Airflow로 바꾸는 일은 조직마다 운영 부담과 기대 효과를 다르게 평가했습니다. 기술 취향이 아니라 운영 기준으로 풀었습니다. 인프라 조직과 먼저 직접 논의해 각자가 보는 부담과 효과를 맞춰 보고, 남은 간극은 판단 기준을 드러내는 방식으로 좁혔습니다. 사람이 정한 시각에 도는 태스크 스케줄이 아니라 스케줄과 센싱(계약 기준)으로 흐르면 배치 시간이 크게 줄고 오류 지점의 재수행이 명확해진다는 기준을 정리해 기술 경영진에 보고했고, 인프라 운영(클러스터·워커)과 데이터 운영(DAG·계약)의 소유 경계를 새로 그었습니다. 실행기 전환은 한 팀의 요구가 아니라 조직 간 합의된 과제가 됐습니다.',
  scopeNote:
    '실시간 경로는 의도적으로 이번 재설계 범위 밖에 두었습니다. 배치의 착지 계약을 먼저 고정하지 않으면 실시간도 같은 문제를 반복하기 때문입니다.',
  stages: [
    {
      stage: '수집 · 배치',
      problem: '여러 홉을 거치는 새벽 배치 릴레이, 실패 지점 불명',
      didWhat: '일 파티션·멱등 재실행 DAG 표준 운영. 재설계에서 착지 계약·Manifest·Watermark와 제어면/실행면 분리 설계',
      stack: ['Airflow', 'S3 / Athena', 'Parquet']
    },
    {
      stage: '수집 · 실시간',
      problem: '이벤트 기간·홈 슬롯처럼 "지금"을 봐야 하는 요구',
      didWhat: 'WebFlux API → Kafka(AWS 관리형) → 컨슈머 → S3(json.gz) → Athena → Redshift 가공 경로. 구현은 이전 팀원이 했고 지금은 제가 운영하며, Kafka 운영은 인프라 조직이 맡습니다',
      stack: ['WebFlux', 'Kafka', 'S3', 'Athena', 'Redshift']
    },
    {
      stage: '가공',
      problem: '조직마다 다른 KPI 정의, 비즈니스 로직 소유권 분산',
      didWhat: 'l1~l5 레이어, 연간 집계·KPI 마트, RFM·선호 데이터 모델, 스키마 변경 이력, 일일 품질 검증 DAG',
      stack: ['SQL', 'Python', 'Redshift Serverless']
    },
    {
      stage: '제공 · API·시각화',
      problem: '실무자가 SQL 없이 반복 의사결정을 해야 함',
      didWhat: 'Kotlin API와 React 사내 시각화 사이트. 권한·캐시·비동기 조회, 서빙 캐시',
      stack: ['Kotlin', 'React', 'PostgreSQL', 'Redis']
    },
    {
      stage: '이해 · 카탈로그',
      problem: '누가 만들었는지 알아야 쓰는 DW',
      didWhat: '지식 그래프 기반 DW 데이터 맵. 계보·지표 정의·해석 규칙을 한곳에서',
      stack: ['BigQuery', 'LLM', '정적 HTML 컴파일']
    },
    {
      stage: '안정 운영',
      problem: '피크·장애·담당자 부재',
      didWhat: 'Redshift Serverless 도입, 장애 대응과 재발 방지, API 캐싱·ETag, 품질 검증과 알림, 운영 매뉴얼',
      stack: ['모니터링', 'Slack 알림']
    }
  ],
  diagram: `flowchart TB
  subgraph ASIS[운영 중]
    direction LR
    A0[원천 DB 복제본] --> B0[온프레미스 배치] --> C0[전송 릴레이] --> D0[S3 · Athena 원천 계층]
    R0[실시간 · API → Kafka → S3 json.gz] --> D0
    D0 --> E0[마트 계층 l1 → l5] --> F0[API · 시각화 · BI · 서빙 캐시]
  end
  subgraph TOBE[설계]
    direction LR
    A1[원천 DB 복제본] --> W1[엣지 워커 · 추출 · Parquet]
    S1[중앙 Airflow · 스케줄 · 상태 · 재시도] <-->|HTTPS 태스크 계약| W1
    W1 --> R1[Raw Landing · 검증 · Manifest] --> P1[Publish · Manifest]
    P1 -->|Manifest 감지| M1[마트 Airflow · 품질 · l2 / l3] --> F1[API · 시각화 · BI]
    G1[거버넌스 · 비용 · 권한 · SLA · 모니터링] -.-> W1 & R1 & M1
  end
  ASIS ==>|계약 먼저, 실행기는 나중| TOBE`
};
